import { request } from '../api/request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

export type ExposureStatistics = {
  count?: number | string
  currency_code?: string
  speed?: number | string
  three_month_amount?: string
  amount?: number | string
}

export type ExposureCategoryItem = {
  alias?: string
  code?: string
  name?: string
}

export type ExposureBlackPlatformItem = {
  alias?: string
  code?: string
  icon?: string
  ico?: string
  localShortName?: string
  logo?: string
  name?: string
  traderCode?: string
}

export type ExposureImageItem = {
  list?: string
  detail?: string
  url?: string
}

export type ExposureSubjectItem = {
  code?: string
  showName?: string
  ico?: string
}

export type ExposureListItem = {
  commentGradeId?: string
  title?: string
  content?: string
  negativeType?: string
  country?: string
  publishTime?: string | number
  traderIcon?: string
  traderCode?: string
  traderName?: string
  images?: ExposureImageItem[]
  aggData?: {
    subject?: ExposureSubjectItem
  }
}

export type ExposureRevelationPageData = {
  categories: ExposureCategoryItem[]
  list: ExposureListItem[]
  total: number
}

export type ReviewExposurePageData = ExposureRevelationPageData & {
  isNotFound: boolean
}

export const REVIEW_REVELATION_ROUTES = 'wikicommunity$fxgradenegativecomment'

export type ExposureRevelationListParams = {
  userid?: string
  keyword?: string
  pageIndex?: number
  pageSize?: number
  negativeType?: string
}

export type ExposureBlackPlatformRankingResponse =
  | ExposureBlackPlatformItem[]
  | {
      data?: {
        result?: {
          weekRankings?: ExposureBlackPlatformItem[]
          totalRankings?: ExposureBlackPlatformItem[]
        }
        traderRank?: ExposureBlackPlatformItem[]
        weekRankings?: ExposureBlackPlatformItem[]
        totalRankings?: ExposureBlackPlatformItem[]
      }
      list?: ExposureBlackPlatformItem[]
      weekRankings?: ExposureBlackPlatformItem[]
      totalRankings?: ExposureBlackPlatformItem[]
    }

export type ExposureStatisticsResponse = {
  data?: {
    wikifxforum?: {
      wikifxforum_statistics?: {
        data?: ExposureStatistics | null
      } | null
    } | null
  } | null
  wikifxforum?: {
    wikifxforum_statistics?: {
      data?: ExposureStatistics | null
    } | null
  } | null
} | null

export type MediationRankVisibilityResponse = {
  mediate_rank?: number | string
  data?: {
    mediate_rank?: number | string
  } | null
} | null

/** 只要接口返回 `mediate_rank=1`，就展示调解中心 tab。 */
const normalizeMediationRankVisibility = (response: MediationRankVisibilityResponse) => {
  const mediateRank = response?.data?.mediate_rank ?? response?.mediate_rank

  return mediateRank === 1 || mediateRank === '1'
}

export type ExposureRevelationResponse = {
  data?: {
    wikicommunity?: {
      fxnegativecommentpagelist?: {
        data?: {
          list?: ExposureListItem[]
          total?: number
        } | null
      } | null
      fxgradenegativecomment?: {
        data?: ExposureCategoryItem[] | null
      } | null
    } | null
  } | null
  wikicommunity?: {
    fxnegativecommentpagelist?: {
      data?: {
        list?: ExposureListItem[]
        total?: number
      } | null
    } | null
    fxgradenegativecomment?: {
      data?: ExposureCategoryItem[] | null
    } | null
  } | null
} | null

/** 统一取聚合接口根节点，兼容 data 包装结构。 */
const getExposureResponseRoot = <T extends Record<string, unknown>>(response: { data?: T | null } & T) => {
  return response?.data || response
}

/** 解析近一月调解统计接口返回。 */
export const normalizeExposureStatisticsData = (response: ExposureStatisticsResponse) => {
  const responseRoot = getExposureResponseRoot(response || {}) as {
    wikifxforum?: NonNullable<ExposureStatisticsResponse>['wikifxforum']
  }

  return responseRoot.wikifxforum?.wikifxforum_statistics?.data || null
}

/** 解析最新曝光列表和分类接口返回。 */
export const normalizeExposureRevelationData = (response: ExposureRevelationResponse): ExposureRevelationPageData => {
  const responseRoot = getExposureResponseRoot(response || {}) as {
    wikicommunity?: NonNullable<ExposureRevelationResponse>['wikicommunity']
  }
  const communityData = responseRoot.wikicommunity
  const categories = (communityData?.fxgradenegativecomment?.data || []).filter(
    (item: ExposureCategoryItem) => item?.code !== '208005',
  )

  return {
    categories,
    list: communityData?.fxnegativecommentpagelist?.data?.list || [],
    total: Number(communityData?.fxnegativecommentpagelist?.data?.total || 0),
  }
}

/** 获取曝光页右侧黑平台榜单。 */
export const getBlackPlatformRanking = async (): Promise<ExposureBlackPlatformRankingResponse> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/feedback/week-ranking-and-bottom',
    method: 'GET' as const,
  }

  if (import.meta.server) {
    return (await useServerFetch<ExposureBlackPlatformRankingResponse>(requestConfig)) || []
  }

  return (await request<unknown, ExposureBlackPlatformRankingResponse>(requestConfig)) || []
}

/** 曝光聚合接口兼容性包装。 */
export const invokeExposureRoute = async <T = unknown>(
  route: string,
  data: Record<string, unknown>,
  apiOverride?: ReturnType<typeof useEnvApi>,
): Promise<T | null> => {
  const api = apiOverride ?? useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.testwikidatacenter : api.homeClient,
    url: `${WIKIBITWEBV1}/invoke-v3/v3`,
    method: 'POST' as const,
    data,
    headers: {
      Routes: route,
    },
  }

  if (import.meta.server) {
    return (await useServerFetch<T>(requestConfig)) || null
  }

  return (await request<unknown, T>(requestConfig)) || null
}

/** 获取调解中心 tab 是否展示，`mediate_rank=1` 时展示。 */
export const getMediationRankVisibility = async (): Promise<boolean> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/trader/detail/card/safeguard/is-show-rank-or-detail',
    method: 'GET' as const,
  }

  try {
    if (import.meta.server) {
      const d = (await useServerFetch<MediationRankVisibilityResponse>(requestConfig)) || null
      return normalizeMediationRankVisibility(d)
    }
    const t = (await request<unknown, MediationRankVisibilityResponse>(requestConfig)) || null
    return normalizeMediationRankVisibility(t)
  } catch {
    return false
  }
}
